import html2canvas from 'html2canvas';
import { sanitizeExportColors } from '../../../../../utils/exportColors';
import { jsPDF } from 'jspdf';
import {
    ensureKalpurushFontLoaded,
    FAMILY_CARD_FONT_FAMILY,
} from '../family-card-form/familyCardFont';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { BASELINE_RATIO, LEADER, LINE_BOX } from './embassyBirthMetrics';

const EXPORT_SCALE = 2;

/**
 * Even with the line box pinned, html2canvas lands every baseline lower than
 * the browser does. Probed through this exporter (spans on known baselines,
 * the PDF rendered back, headline rows read off it) the drift is 3.75 pt
 * ±0.5 across 6-10 px — the sizes this form uses — rather than the 2.5 pt the
 * larger Form-11 text measured. Lift each line by it so the export sits where
 * CSS says.
 */
const EXPORT_BASELINE_DRIFT_PT = 3.75;

function prepareEmbassyBirthClone(doc) {
    doc.querySelectorAll('[data-embassy-birth-page]').forEach((root) => {
        // html2canvas can't parse oklch/lab/color() — rewrite any to rgb first.
        sanitizeExportColors(root);
        root.style.overflow = 'visible';
        root.style.transform = 'none';
        root.style.fontFamily = FAMILY_CARD_FONT_FAMILY;
        // html2canvas puts a line's baseline at the bottom of its line box
        // rather than where CSS does. Shrinking the line box to the baseline
        // offset lands the text on the same baselines as the preview, and an
        // explicit height keeps the room below it that the dots and descenders
        // need before the overflow clip bites.
        root.querySelectorAll('span').forEach((el) => {
            el.style.verticalAlign = 'baseline';
            if (Number.isNaN(parseFloat(el.style.top))) return;
            const size = parseFloat(el.style.fontSize) || 0;
            if (!size) return;
            // The preview's baseline snap is for Chrome's layout only.
            el.style.marginTop = '0px';
            el.style.lineHeight = `${BASELINE_RATIO * size}px`;
            el.style.height = `${LINE_BOX * size}px`;
            el.style.top = `${parseFloat(el.style.top) - EXPORT_BASELINE_DRIFT_PT}px`;
        });
        // Answers set mid-sentence keep their line's baseline; they only need ink.
        root.querySelectorAll('[data-inline-value]').forEach((el) => {
            el.style.verticalAlign = 'baseline';
        });
        root.querySelectorAll('[data-field-value]').forEach((el) => {
            el.style.overflow = 'hidden';
            el.style.textOverflow = 'ellipsis';
            // Latin descenders drop further than the form's own text; give the
            // clip box extra room so g, j, p, q and y survive the export.
            const size = parseFloat(el.style.fontSize) || 0;
            if (size) el.style.height = `${size * 2}px`;
        });
        // html2canvas re-measures text, so the leaders keep their printed pitch.
        root.querySelectorAll('[data-leader]').forEach((el) => {
            const size = parseFloat(el.style.fontSize) || 0;
            const pitch = parseFloat(el.getAttribute('data-leader-pitch')) || 0;
            if (size && pitch) el.style.letterSpacing = `${LEADER.letterSpacing(size, pitch)}px`;
            el.style.overflow = 'hidden';
            el.style.whiteSpace = 'nowrap';
        });
    });
}

/**
 * Clone off-screen at 1:1 scale — html2canvas clips text inside CSS transform: scale().
 * @param {HTMLElement} element
 */
function cloneForExport(element) {
    const clone = element.cloneNode(true);
    const width = element.scrollWidth;

    const wrapper = document.createElement('div');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.style.cssText = [
        'position:fixed',
        'left:-20000px',
        'top:0',
        `width:${width}px`,
        'overflow:visible',
        'pointer-events:none',
        'z-index:-1',
    ].join(';');

    clone.style.transform = 'none';
    clone.style.transformOrigin = 'top left';
    clone.style.overflow = 'visible';

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    return { clone, wrapper, width };
}

/**
 * The `[data-embassy-birth-page]` becomes one A4 page — the form and its
 * tear-off slip are printed on a single sheet.
 * @param {HTMLElement} element The node wrapping the page.
 * @returns {Promise<Blob>}
 */
export async function generateEmbassyBirthPdfBlob(element) {
    await ensureKalpurushFontLoaded();
    const { clone, wrapper } = cloneForExport(element);

    try {
        await document.fonts.ready;
        const pages = Array.from(clone.querySelectorAll('[data-embassy-birth-page]'));
        const targets = pages.length ? pages : [clone];

        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        for (let i = 0; i < targets.length; i += 1) {
            const target = targets[i];
            const width = target.scrollWidth;
            const height = target.scrollHeight;

            // eslint-disable-next-line no-await-in-loop
            const canvas = await html2canvas(target, {
                scale: EXPORT_SCALE,
                width,
                height,
                windowWidth: width,
                windowHeight: height,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: '#ffffff',
                onclone: prepareEmbassyBirthClone,
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;
            let drawWidth = pdfWidth;
            let drawHeight = imgHeight;

            if (imgHeight > pdfHeight) {
                const fit = pdfHeight / imgHeight;
                drawWidth = pdfWidth * fit;
                drawHeight = pdfHeight;
            }

            if (i > 0) pdf.addPage();
            const x = (pdfWidth - drawWidth) / 2;
            pdf.addImage(imgData, 'JPEG', x, 0, drawWidth, drawHeight, undefined, 'FAST');
        }

        return pdf.output('blob');
    } finally {
        wrapper.remove();
    }
}

export async function downloadEmbassyBirthPdf(element, filename = 'embassy-birth-registration-form.pdf') {
    const blob = await generateEmbassyBirthPdfBlob(element);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/**
 * Print the generated PDF from the current page. `printBlob` renders the pages
 * into a hidden same-origin iframe and prints that, so the print dialog opens
 * right here instead of in a second tab (it only falls back to a pop-up tab if
 * rendering fails).
 * @param {HTMLElement} element
 * @param {string} popupBlockedMessage
 */
export async function printEmbassyBirthForm(element, popupBlockedMessage) {
    const blob = await generateEmbassyBirthPdfBlob(element);
    const opened = await printBlob(blob);
    if (!opened) throw new Error(popupBlockedMessage);
}
